import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Leaf, Globe, Factory, BarChart3, Sprout, CheckCircle, Quote, Eye, X } from 'lucide-react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { useData } from '../context/DataContext';
import Translate from '../components/Translate';

const Home = () => {
  const { data } = useData();
  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [isChairmanModalOpen, setIsChairmanModalOpen] = useState(false);

  // Data fetching from Firestore context
  const chairman = data?.about?.chairman || {
    name: 'Shri. Rajendra (Appasaheb) Deshmukh',
    position: 'Founder & Chairman',
    message: 'Welcome to Sangram Cane Agro Limited. We are committed to fostering a sustainable ecosystem that brings prosperity to our farmers while producing premium grade sugar and clean energy. Our state-of-the-art co-generation and ethanol processing plants are built to serve both our nation and environment.',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=600'
  };

  const products = (data?.products || []).slice(0, 3);

  // News items combined
  const newsItems = [
    ...(data?.news?.latest || []),
    ...(data?.news?.upcoming || []),
    ...(data?.news?.press || [])
  ].slice(0, 3);

  const galleryImages = (data?.gallery?.images || []).slice(0, 6);

  const stats = data?.stats || [];

  return (
    <div className="flex flex-col">
      {/* ── HERO SECTION ── */}
      <section className="relative min-h-[520px] h-[80vh] sm:h-[85vh] lg:h-[90vh] flex items-center justify-start overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="/VN20260625_114019.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/15"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/25"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-white leading-[1.1] mb-6 tracking-tight">
              <Translate>Sangram Cane Agro</Translate>
            </h1>

            <p className="text-sm md:text-base text-white/80 font-body leading-relaxed mb-10 max-w-xl">
              <Translate>Purity You Can Trust, Sweetness You Can Believe In. 100% Natural Jaggery & Jaggery Powder – From Our Farms to Your Kitchen, delivering pure, chemical-free sweetness in every bite.</Translate>
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/products"
                className="px-6 py-3.5 bg-emerald text-white rounded-xl font-heading font-bold text-xs tracking-wide
                           hover:bg-sage hover:text-forest transition-all duration-300 group flex items-center shadow-lg shadow-emerald/20"
              >
                <Translate>Explore Our Products</Translate>
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
              </Link>
              <Link
                to="/about"
                className="px-6 py-3.5 bg-white/10 backdrop-blur-md text-white border border-white/20 rounded-xl font-heading font-bold text-xs tracking-wide
                           hover:bg-white/20 transition-all duration-300"
              >
                <Translate>Our Heritage</Translate>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section ref={statsRef} className="bg-forest relative overflow-hidden">
        <div className="absolute inset-0 pattern-overlay"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-5 divide-x divide-white/10">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={statsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                className="text-center py-6 px-4 group"
              >
                <h3 className="text-xl md:text-2xl font-heading font-extrabold text-sage mb-1">
                  {statsInView && <CountUp end={stat.value} duration={2.5} separator="," />}
                  {stat.suffix}
                </h3>
                <p className="text-white/40 font-body font-semibold text-[9px] uppercase tracking-[0.15em]">
                  <Translate>{stat.label}</Translate>
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT PREVIEW ── */}
      <section className="py-16 lg:py-20 bg-section-gradient overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image Side */}
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative z-10"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-premium-lg group">
                  <motion.img
                    whileHover={{ scale: 1.03 }}
                    transition={{ duration: 0.8 }}
                    src="https://images.pexels.com/photos/37125625/pexels-photo-37125625.jpeg"
                    alt="Factory"
                    className="w-full h-[320px] lg:h-[380px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="absolute -bottom-8 -right-4 lg:-right-8 z-20 w-[55%]"
                >
                  <div className="overflow-hidden rounded-2xl shadow-premium-lg border-4 border-white">
                    <img
                      src="https://images.pexels.com/photos/11466855/pexels-photo-11466855.jpeg"
                      alt="Sugarcane"
                      className="w-full h-[180px] object-cover"
                    />
                  </div>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 100, delay: 0.5 }}
                className="absolute top-6 -right-2 z-30"
              >
                <div className="bg-white p-1 rounded-2xl shadow-premium-lg">
                  <div className="bg-forest text-white px-4 py-4 rounded-xl text-center">
                    <p className="text-2xl font-heading font-extrabold leading-none text-sage">10+</p>
                    <p className="text-[8px] uppercase tracking-[0.2em] font-bold mt-1 text-white/55">
                      <Translate>Years of</Translate><br /><Translate>Excellence</Translate>
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Content Side */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center space-x-2.5 mb-4">
                <div className="p-2 bg-emerald/10 rounded-lg">
                  <Leaf className="text-emerald" size={16} />
                </div>
                <span className="section-label"><Translate>Our Legacy & History</Translate></span>
              </div>

              <h2 className="section-title mb-6">
                <Translate>A Heritage of Innovation & Prosperity</Translate>
              </h2>

              <div className="gold-divider mb-6"></div>

              <p className="text-slate text-sm lg:text-base leading-relaxed mb-4">
                <Translate>Sangram Cane Agro is a farmer-owned and farmer-driven company committed to supporting the agricultural community while delivering premium-quality natural products. Using carefully selected sugarcane grown in the fertile fields of Sangli district, we produce Natural Jaggery, Jaggery Powder, Bagasse Briquettes, and Organic Fertilizer by combining traditional practices with modern processing standards. Every stage of production is carried out with strict hygiene, quality control, and food safety measures to ensure that our customers receive 100% natural, chemical-free, and nutritious products.</Translate>
              </p>

              <p className="text-slate text-sm lg:text-base leading-relaxed mb-8">
                <Translate>Our mission is to provide farmers with fair value for their hard work while offering consumers pure, healthy, and authentic sweetness they can trust. We are guided by four core values—quality, transparency, sustainability, and customer satisfaction.</Translate>
              </p>

              <Link
                to="/about"
                className="btn-premium inline-flex items-center group text-xs py-3"
              >
                <Translate>Learn More About Us</Translate>
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── CHAIRMAN'S MESSAGE SECTION (UNIQUE LAYOUT: DARK FOREST GREEN WITH IMAGE ON THE RIGHT) ── */}
      <section className="py-16 lg:py-20 bg-forest text-white relative overflow-hidden">
        {/* Geometric dots accent in background */}
        <div className="absolute inset-0 pattern-overlay"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/3"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

            {/* Statement Text (Left Column - alternated position) */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-7 space-y-6"
            >
              <div className="flex items-center space-x-2.5">
                <div className="p-2 bg-white/10 rounded-lg">
                  <Quote className="text-sage" size={16} />
                </div>
                <span className="text-sage font-heading font-extrabold uppercase tracking-[0.2em] text-xs">
                  <Translate>Founder's Address</Translate>
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-white leading-tight">
                <Translate>Welcome to Sangram Cane Agro</Translate>
              </h2>

              <div className="w-12 h-[2px] bg-gradient-to-r from-sage to-emerald rounded-full"></div>

              <div className="text-white/80 text-sm lg:text-base leading-relaxed italic pl-5 border-l-2 border-sage/40">
                <p>
                  "
                  <Translate>
                    A visionary leader whose unwavering dedication, determination, and commitment to excellence have earned him the admiration and trust of the community, Late. Vithhalrao ( Bapusaheb ) Yesugade is widely regarded as one of the most respected pioneers in the region's agro-industrial sector. Under his guidance, Sangram Cane Agro has achieved monumental growth, fostering farmer empowerment and industrial excellence across Maharashtra.
                  </Translate>
                  "
                </p>
              </div>

              <div className="pt-2 pl-5">
                <h4 className="text-lg font-heading font-bold text-white"><Translate>{chairman.name}</Translate></h4>
                <p className="text-sage font-heading font-bold text-xs uppercase tracking-wider mt-0.5"><Translate>{chairman.position}</Translate></p>
              </div>

              <div className="pt-4 pl-5">
                <button
                  onClick={() => setIsChairmanModalOpen(true)}
                  className="px-6 py-3 bg-transparent border-2 border-sage text-sage rounded-xl font-heading font-semibold hover:bg-sage hover:text-forest transition-all duration-300 inline-flex items-center group text-xs cursor-pointer"
                >
                  <Translate>View Chairman Address</Translate>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
                </button>
              </div>
            </motion.div>

            {/* Portrait Image (Right Column - alternated position) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5"
            >
              <img
                src={chairman.image || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=600'}
                alt={chairman.name}
                className="rounded-xl shadow-premium-xl w-full object-cover aspect-[3/4]"
              />
            </motion.div>

          </div>
        </div>
      </section>

      {/* Chairman Full Message Modal */}
      <AnimatePresence>
        {isChairmanModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsChairmanModalOpen(false)}
            className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-charcoal/75 backdrop-blur-md overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl border border-gray-150 shadow-premium-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden relative"
            >
              {/* Sticky Top Header with Close Button */}
              <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md px-6 py-4 border-b border-gray-100 flex justify-between items-center flex-shrink-0">
                <div className="flex items-center space-x-2 text-emerald">
                  <Quote size={18} />
                  <span className="text-xs font-heading font-extrabold uppercase tracking-widest text-charcoal">
                    <Translate>Leadership Address</Translate>
                  </span>
                </div>
                <button
                  onClick={() => setIsChairmanModalOpen(false)}
                  className="p-2 bg-gray-100 hover:bg-emerald hover:text-white rounded-full text-charcoal transition-colors cursor-pointer shadow-sm flex items-center justify-center"
                  aria-label="Close modal"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Scrollable Body Content */}
              <div className="overflow-y-auto flex-1 p-5 sm:p-8">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                  {/* Left Portrait Column */}
                  <div className="md:col-span-5 bg-ivory/50 p-6 rounded-2xl flex flex-col items-center justify-center border border-gray-100">
                    <div className="relative w-40 h-52 sm:w-48 sm:h-64 md:w-full md:h-80 rounded-2xl overflow-hidden shadow-premium border-2 border-sage/20">
                      <img
                        src={chairman.image || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=600'}
                        alt={chairman.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="text-center mt-4">
                      <h3 className="text-lg sm:text-xl font-heading font-extrabold text-charcoal">
                        <Translate>{chairman.name}</Translate>
                      </h3>
                      <p className="text-emerald font-heading font-bold text-xs uppercase tracking-wider mt-1">
                        <Translate>{chairman.position}</Translate>
                      </p>
                    </div>
                  </div>

                  {/* Right Message Column */}
                  <div className="md:col-span-7 space-y-4 sm:p-2">
                    <div className="text-slate text-sm sm:text-base leading-relaxed space-y-4 font-body">
                      {chairman.message && chairman.message.split('\n').map((paragraph, idx) => (
                        <p key={idx}>
                          <Translate>{paragraph}</Translate>
                        </p>
                      ))}
                    </div>

                    <div className="border-t border-gray-100 pt-6 mt-6 flex justify-end">
                      <button
                        onClick={() => setIsChairmanModalOpen(false)}
                        className="px-6 py-2.5 bg-emerald text-white hover:bg-forest rounded-xl font-heading font-bold text-xs uppercase tracking-wider transition-colors shadow-md cursor-pointer"
                      >
                        <Translate>Close Address</Translate>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── WHY CHOOSE US SECTION ── */}
      <section className="py-16 lg:py-20 bg-white relative overflow-hidden border-t border-gray-100 font-poppins">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="section-label block mb-3 text-emerald font-heading font-extrabold uppercase tracking-widest text-xs">
              <Translate>WHY CHOOSE US</Translate>
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-charcoal mb-4 tracking-tight">
              <Translate>Why Choose Sangram Cane Agro?</Translate>
            </h2>
            <div className="gold-divider mx-auto mb-4"></div>
            <p className="text-slate text-sm lg:text-base leading-relaxed">
              <Translate>Delivering premium-quality sugarcane products with uncompromising standards, sustainable practices, and customer-focused service.</Translate>
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                num: '01',
                title: 'Certified Quality',
                subtitle: 'FSSAI Approved Manufacturing',
                desc: 'Our products are manufactured in certified facilities that adhere to the highest food safety and quality standards.',
                icon: CheckCircle
              },
              {
                num: '02',
                title: 'Authentic Natural Taste',
                subtitle: 'Pure Sugarcane Goodness',
                desc: 'We preserve the natural sweetness and rich flavor of sugarcane through carefully controlled processing.',
                icon: Leaf
              },
              {
                num: '03',
                title: 'Competitive Pricing',
                subtitle: 'Best Value for Every Customer',
                desc: 'Premium quality products offered at competitive prices for both retail and wholesale buyers.',
                icon: BarChart3
              },
              {
                num: '04',
                title: 'Reliable & Timely Delivery',
                subtitle: 'On-Time Supply Across India',
                desc: 'Efficient logistics ensure safe and prompt delivery throughout Maharashtra and across India.',
                icon: Globe
              },
              {
                num: '05',
                title: 'Retail & Bulk Supply',
                subtitle: 'Flexible Order Quantities',
                desc: 'We cater to households, retailers, distributors, wholesalers, and industrial buyers with customized supply solutions.',
                icon: Factory
              },
              {
                num: '06',
                title: 'Sustainable & Trusted Manufacturing',
                subtitle: 'Committed to Excellence',
                desc: 'Produced using environmentally responsible practices with a strong focus on consistency, reliability, and customer satisfaction.',
                icon: Sprout
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05, duration: 0.5 }}
                className="group p-8 bg-white border border-gray-150 rounded-2xl shadow-premium
                           hover:shadow-premium-xl hover:-translate-y-1.5 hover:border-emerald/30 transition-all duration-300 relative flex flex-col justify-between"
              >
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <div className="w-14 h-14 bg-emerald/10 text-emerald rounded-2xl flex items-center justify-center
                                   group-hover:bg-emerald group-hover:text-white transition-all duration-300 shadow-sm">
                      <item.icon size={26} />
                    </div>
                    <span className="text-2xl font-heading font-black text-gray-200 group-hover:text-emerald/20 transition-colors">
                      {item.num}
                    </span>
                  </div>

                  <span className="text-[10px] font-heading font-extrabold uppercase tracking-widest text-forest bg-forest/10 px-3 py-1 rounded-md mb-3 inline-block">
                    <Translate>{item.subtitle}</Translate>
                  </span>

                  <h3 className="text-xl font-heading font-extrabold mb-3 text-charcoal group-hover:text-emerald transition-colors leading-snug">
                    <Translate>{item.title}</Translate>
                  </h3>

                  <p className="text-slate text-xs leading-relaxed font-body">
                    <Translate>{item.desc}</Translate>
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ALL PRODUCTS SECTION ── */}
      {products.length > 0 && (
        <section className="py-16 lg:py-20 bg-ivory border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4">
              <div>
                <span className="section-label block mb-2"><Translate>Product Division</Translate></span>
                <h2 className="section-title"><Translate>Our Quality Products</Translate></h2>
              </div>
              <Link
                to="/products"
                className="inline-flex items-center space-x-1.5 text-emerald font-heading font-bold text-xs uppercase tracking-wider hover:text-forest transition-colors group cursor-pointer"
              >
                <span><Translate>View All Products</Translate></span>
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="premium-card overflow-hidden group flex flex-col h-full bg-white"
                >
                  <div className="relative h-48 overflow-hidden flex-shrink-0 bg-mist">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-md px-2.5 py-1 rounded-lg text-[9px] font-heading font-bold uppercase tracking-wider text-emerald shadow-sm">
                      <Translate>{product.category}</Translate>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-grow justify-between">
                    <div>
                      <h3 className="font-heading font-bold text-charcoal text-base mb-2 group-hover:text-emerald transition-colors">
                        <Translate>{product.name}</Translate>
                      </h3>
                      <p className="text-slate text-xs line-clamp-3 leading-relaxed mb-4">
                        <Translate>{product.desc}</Translate>
                      </p>
                    </div>
                    <div className="flex justify-end items-center pt-3 border-t border-gray-150">
                      <Link
                        to={`/products/${product.id}`}
                        className="flex items-center space-x-1 text-emerald font-heading font-bold text-xs hover:text-forest transition-colors"
                      >
                        <span><Translate>Details</Translate></span>
                        <ArrowRight size={12} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── GALLERY IMAGES SECTION ── */}
      {galleryImages.length > 0 && (
        <section className="py-16 lg:py-20 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4">
              <div>
                <span className="section-label block mb-2"><Translate>Our Journey</Translate></span>
                <h2 className="section-title"><Translate>Moments & Highlights</Translate></h2>
              </div>
              <Link
                to="/gallery"
                className="inline-flex items-center space-x-1.5 text-emerald font-heading font-bold text-xs uppercase tracking-wider hover:text-forest transition-colors group cursor-pointer"
              >
                <span><Translate>View Full Gallery</Translate></span>
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((img) => (
                <Link
                  key={img.id}
                  to="/gallery"
                  className="group relative aspect-[4/3] h-60 sm:h-64 overflow-hidden rounded-2xl bg-gray-55 border border-gray-150 shadow-premium-lg cursor-pointer"
                >
                  <img
                    src={img.url}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-forest/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white/95 flex items-center justify-center text-emerald shadow-lg">
                      <Eye size={18} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── NEWS SECTION ── */}
      {newsItems.length > 0 && (
        <section className="py-16 lg:py-20 bg-ivory border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4">
              <div>
                <span className="section-label block mb-2"><Translate>Newsroom</Translate></span>
                <h2 className="section-title"><Translate>Latest Updates</Translate></h2>
              </div>
              <Link
                to="/news"
                className="inline-flex items-center space-x-1.5 text-emerald font-heading font-bold text-xs uppercase tracking-wider hover:text-forest transition-colors group cursor-pointer"
              >
                <span><Translate>View All News</Translate></span>
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {newsItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-premium flex flex-col justify-between group hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-gray-50">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-103"
                    />
                  </div>
                  <div className="p-5 flex-grow flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-center text-[9px] text-slate/50 font-heading font-bold uppercase tracking-wider mb-2">
                        <span className="px-2 py-0.5 bg-emerald/10 text-emerald rounded-md">
                          <Translate>{item.category || 'Update'}</Translate>
                        </span>
                        <span>{item.date}</span>
                      </div>
                      <h4 className="font-heading font-bold text-charcoal text-sm leading-snug line-clamp-2 min-h-[2.5rem]">
                        <Translate>{item.title}</Translate>
                      </h4>
                    </div>
                    <div className="pt-4 border-t border-gray-50 flex justify-end">
                      <Link
                        to={`/news/${item.id}`}
                        className="inline-flex items-center space-x-1 text-emerald font-heading font-bold text-xs hover:text-forest transition-colors"
                      >
                        <span><Translate>Read Story</Translate></span>
                        <ArrowRight size={12} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA BANNER ── */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-forest rounded-2xl p-10 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 pattern-overlay"></div>
            <div className="absolute top-0 right-0 w-80 h-80 bg-emerald/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-gold/10 rounded-full blur-3xl"></div>

            <div className="relative z-10 max-w-3xl mx-auto text-center">
              <div className="gold-divider mx-auto mb-6"></div>
              <h2 className="text-2xl md:text-4xl font-heading font-extrabold text-white mb-6 leading-tight tracking-tight">
                <Translate>Your Trusted Partner for Pure Jaggery & Sustainable Agriculture</Translate>
              </h2>
              <p className="text-white/80 text-sm md:text-base mb-8 font-body max-w-xl mx-auto leading-relaxed">
                <Translate>Contact us for premium-quality jaggery, jaggery powder, and organic fertilizer. We welcome farmers, distributors, and business partners to grow together.</Translate>
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3.5 bg-sage text-forest rounded-xl font-heading font-bold text-xs tracking-wide
                           hover:bg-white hover:text-forest shadow-lg shadow-gold/20 transition-all duration-300 group"
              >
                <Translate>Contact Our Factory</Translate>
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
